/* oxlint-disable no-console */

import { UsdtPrecision } from '@flipster/utils/constants/currency';
import { datadog, tradeLogger } from '@flipster/utils/datadog';

import {
  ChartingLibraryWidgetOptions,
  IChartingLibraryWidget,
  widget as TVWidget,
  Timezone,
  TradingTerminalWidgetOptions,
  type IChartWidgetApi,
  type ResolutionString,
} from '../lib/charting_library';
import { tvLogger } from './logger';
import { TradingViewDataManager } from './misc/data-manager';
import { symbolNameToInfo } from './misc/helper';

type EventMap = {
  initialize: undefined;
  isLoading: boolean;
  isReady: boolean;
  onSymbolChanged: string;
  onIntervalChanged: ResolutionString;
  onFirstDataLoaded: { symbol: string; resolution: ResolutionString; symbolChanged: boolean; intervalChanged: boolean };
};
type Events = keyof EventMap;
type Callback<T> = (value: T) => void;
type Listeners = { [K in Events]: Callback<EventMap[K]>[] };
export type TvEventHandler<E extends keyof EventMap> = (payload: EventMap[E]) => void;

const tradingViewSupportedTimezones = [
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Tunis',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Caracas',
  'America/Chicago',
  'America/El_Salvador',
  'America/Juneau',
  'America/Lima',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Phoenix',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Almaty',
  'Asia/Ashkhabad',
  'Asia/Bahrain',
  'Asia/Bangkok',
  'Asia/Chongqing',
  'Asia/Colombo',
  'Asia/Dhaka',
  'Asia/Dubai',
  'Asia/Ho_Chi_Minh',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Karachi',
  'Asia/Kathmandu',
  'Asia/Kolkata',
  'Asia/Kuala_Lumpur',
  'Asia/Kuwait',
  'Asia/Manila',
  'Asia/Muscat',
  'Asia/Nicosia',
  'Asia/Qatar',
  'Asia/Riyadh',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Taipei',
  'Asia/Tehran',
  'Asia/Tokyo',
  'Asia/Yangon',
  'Atlantic/Azores',
  'Atlantic/Reykjavik',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Bratislava',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Luxembourg',
  'Europe/Madrid',
  'Europe/Malta',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Riga',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Tallinn',
  'Europe/Vienna',
  'Europe/Vilnius',
  'Europe/Warsaw',
  'Europe/Zurich',
  'Pacific/Auckland',
  'Pacific/Chatham',
  'Pacific/Fakaofo',
  'Pacific/Honolulu',
  'Pacific/Norfolk',
  'US/Mountain',
  'Etc/UTC',
  'exchange',
] satisfies Array<'exchange' | Timezone>;

class TradingView {
  static container = 'tv-chart-container';
  static fontStyle = '10px -apple-system, "system-ui", "Trebuchet MS", Roboto, Ubuntu, sans-serif';

  #widget: IChartingLibraryWidget | undefined;

  public get widget() {
    return this.#widget;
  }

  #_isReady: boolean = false;
  #_isLoading: boolean = true;
  #_symbol: string = 'dummy';
  #_interval: ResolutionString | undefined;
  #listeners: Listeners = {
    initialize: [],
    isReady: [],
    isLoading: [],
    onSymbolChanged: [],
    onIntervalChanged: [],
    onFirstDataLoaded: [],
  };

  constructor(
    widgetOptions: Omit<ChartingLibraryWidgetOptions | TradingTerminalWidgetOptions, 'container' | 'interval'>,
    readonly options: { webview?: boolean } = {},
  ) {
    if (widgetOptions.symbol) this.#symbol = widgetOptions.symbol;

    TradingViewDataManager.getItem('interval').then((savedInterval) => {
      if (
        widgetOptions.timezone != null &&
        tradingViewSupportedTimezones.every((timezone) => timezone !== widgetOptions.timezone) &&
        widgetOptions.custom_timezones?.every((timezone) => (timezone.id as string) !== widgetOptions.timezone) !==
          false
      ) {
        tradeLogger.warn('chart_timezone_not_supported', {
          timezone: widgetOptions.timezone,
        });
      }
      this.#_interval = savedInterval;
      const widget = new TVWidget({
        container: TradingView.container,
        interval: savedInterval,
        ...widgetOptions,
      });
      this.#widget = widget;
      this.#emit('initialize', undefined);

      widget.onChartReady(() => {
        const activeChart = widget.activeChart();

        // app 에서 웹뷰가 제대로 로드 되었는지 체크하기 위한 status
        if (options.webview && window.WebviewReceiver) {
          window.WebviewReceiver.status = 'done';
        }

        // Setting logger
        tvLogger(this, options.webview);

        let isSymbolChange = true;
        let isIntervalChange = true;

        activeChart.onSymbolChanged().subscribe(null, () => {
          isSymbolChange = true;
          this.#isReady = true;
          this.#isLoading = true;
          // control z 연타시 dummy 까지 보이는 이슈 해결 @noel
          this.widget?.clearUndoHistory();
          // price auto 가 자주 꺼지는데 그때 다시 auto 를 켜주는 작업 @kanary
          this.activeChart?.getPanes()[0]?.getMainSourcePriceScale()?.setAutoScale(true);
          if (this.activeChart) this.#emit('onSymbolChanged', this.activeChart.symbol());
        });
        activeChart.onIntervalChanged().subscribe(null, (interval) => {
          isIntervalChange = true;
          this.#isReady = true;
          this.#isLoading = true;
          this.#interval = interval;
          this.#emit('onIntervalChanged', interval);
        });
        activeChart.onDataLoaded().subscribe(null, () => {
          this.#isLoading = false;
          // 실제 이벤트 전파타임은 액션이 가능한 data loaded 이후로
          if (isIntervalChange || isSymbolChange) {
            if (isSymbolChange) {
              datadog.addTiming('onFirstDataLoaded_symbol_change');
            }
            this.#emit('onFirstDataLoaded', {
              symbol: this.symbol,
              resolution: activeChart.resolution(),
              symbolChanged: isSymbolChange,
              intervalChanged: isIntervalChange,
            });
            isIntervalChange = false;
            isSymbolChange = false;
          }
        });

        this.#isReady = true;
        if (this.symbol !== activeChart.symbol()) this.setSymbol(this.symbol);
      });
    });
  }

  public async setSymbol(symbol: string) {
    this.#symbol = symbol;
    await this.ready();
    datadog.addTiming('setSymbol');
    this.activeChart?.setSymbol(symbol);
  }

  on<K extends Events>(event: K, callback: Callback<EventMap[K]>) {
    this.#listeners[event].push(callback);
    return () => this.off(event, callback);
  }

  off<K extends Events>(event: K, callback: Callback<EventMap[K]>) {
    const listeners = this.#listeners[event];
    const index = listeners.indexOf(callback);
    if (index >= 0) listeners.splice(index, 1);
  }

  #emit<K extends Events>(event: K, value: EventMap[K]) {
    for (const cb of this.#listeners[event]) cb(value);
  }

  get enabled(): boolean {
    if (!this.#isReady || this.#isLoading) return false;
    if (this.isDummy) return false;
    if (!this.activeChart) return false;
    return true;
  }

  get activeChart(): IChartWidgetApi | null {
    if (!this.widget) return null;
    if (!this.widget._innerWindow?.()) return null;
    if (!this.widget._innerAPI?.()) return null;
    return this.widget.activeChart();
  }

  get isDummy(): boolean {
    return this.symbol.toLowerCase() === 'dummy';
  }

  ready(): Promise<void> {
    if (this.#isReady) return Promise.resolve();
    return new Promise((resolve) => this.on('isReady', () => resolve()));
  }

  set #isReady(value: boolean) {
    if (this.#_isReady === value) return;
    this.#_isReady = value;
    this.#emit('isReady', value);
  }

  get #isReady() {
    return this.#_isReady;
  }

  set #isLoading(value: boolean) {
    if (this.#_isLoading === value) return;
    this.#_isLoading = value;
    this.#emit('isLoading', value);
  }

  get #isLoading() {
    return this.#_isLoading;
  }

  set #symbol(value: string) {
    // TV's chart.symbol() omits the MID suffix (BTCUSDT.PERP:MID → BTCUSDT.PERP).
    // MARK keeps :MARK. Stripping :MID is safe — no-suffix normalizes back to MID
    // (spot/perp both), so onChartReady recovery (`setSymbol(this.symbol)`) resolves MID again.
    //
    // ROLLBACK(last-price): MM 준비 완료 시 재활성화 — 원본: LAST만 strip, MID는 보존
    // (당시 no-suffix가 LAST로 normalize돼 :MID를 strip하면 spot cold-start가 깨졌음).
    // const showSymbolName = priceType === 'LAST' ? symbol : value;
    const { symbol, priceType } = symbolNameToInfo(value);
    const showSymbolName = priceType === 'MID' ? symbol : value;
    if (this.#_symbol === showSymbolName) return;
    this.#_symbol = showSymbolName;
  }

  get symbol() {
    return this.#_symbol;
  }

  set #interval(value: ResolutionString) {
    TradingViewDataManager.setItem('interval', value);
    this.#_interval = value;
  }

  get interval() {
    return this.#_interval;
  }

  /**
   * 현재 심볼의 가격 정밀도를 계산하는 getter
   */
  get symbolPricePrecision(): number {
    if (!this.activeChart) return UsdtPrecision;
    return Math.floor(Math.log10(this.activeChart.symbolExt()?.pricescale || 10));
  }

  /**
   * 현재 심볼의 quote currency (예: 'USDT', 'USD1'). 못 얻으면 'USDT'로 폴백.
   * 차트 라인 라벨처럼 화폐 단위를 표기해야 하는 곳에서 하드코딩 대신 사용한다.
   */
  get quoteCurrency(): string {
    return (this.activeChart?.symbolExt()?.library_custom_fields?.quoteCurrency as string | undefined) ?? 'USDT';
  }

  createPositionLine() {
    if (!this.enabled || !this.activeChart)
      throw new Error('Cannot createPositionLine: both "enabled" must be true and "activeChart" must be defined.');
    return this.activeChart
      .createPositionLine()
      .setLineStyle(1)
      .setLineLength(100)
      .setBodyFont(TradingView.fontStyle)
      .setQuantityFont(TradingView.fontStyle);
  }

  createOrderLine() {
    if (!this.enabled || !this.activeChart)
      throw new Error('Cannot createOrderLine: both "enabled" must be true and "activeChart" must be defined.');
    return this.activeChart
      .createOrderLine()
      .setLineStyle(1)
      .setLineLength(100)
      .setBodyFont(TradingView.fontStyle)
      .setQuantityFont(TradingView.fontStyle);
  }

  createExecutionShape() {
    if (!this.enabled || !this.activeChart)
      throw new Error('Cannot createExecutionShape: both "enabled" must be true and "activeChart" must be defined.');
    return this.activeChart.createExecutionShape().setFont(TradingView.fontStyle);
  }
}

declare global {
  interface Window {
    WebviewReceiver?: {
      status: 'loading' | 'done';
    };
  }
}

declare module '../lib/charting_library' {
  interface IChartingLibraryWidget {
    _innerWindow(): WindowProxy | null;
    _innerAPI(): WindowProxy | null;
  }
}

export default TradingView;
